import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { StationTabs } from "@/components/StationTabs";
import { PeriodPicker, usePeriod } from "@/components/PeriodPicker";
import { WashDialog, WASH_TYPES } from "@/components/washes/WashDialog";
import { download } from "@/components/phones/PhoneRegister";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useSort, SortTh } from "@/lib/sort";

const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fd = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const TYPE = Object.fromEntries(WASH_TYPES);

export default function WashesPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ items: [], summary: {} });
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [period, setPeriod] = usePeriod();
  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const params = { station_id: sid, date_from: period.date_from || undefined, date_to: period.date_to || undefined };
  const load = useCallback(() => {
    api.get("/washes", { params: { station_id: sid, date_from: period.date_from || undefined, date_to: period.date_to || undefined } }).then((r) => setData(r.data));
  }, [sid, period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data.vehicles));
  }, []);
  const items = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.items.filter((i) => !t || [i.plate, i.place, i.notes, i.station_name].some((x) => (x || "").toLowerCase().includes(t)));
  }, [data.items, q]);
  const places = useMemo(() => [...new Set(data.items.map((i) => i.place))].slice(0, 30), [data.items]);
  const { sorted, sort, toggle } = useSort(items);
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const remove = async (i) => {
    if (!window.confirm(`Eliminare il lavaggio di ${i.plate} del ${fd(i.wash_date)}?`)) return;
    try { await api.delete(`/washes/${i.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = data.summary;

  return (
    <div className="fade-up">
      <PageHeader title="Lavaggi" subtitle="Archivio dei lavaggi dei furgoni: targa, data e luogo.">
        <Button variant="outline" onClick={() => download("/washes/export", params, "lavaggi.xlsx")} data-testid="wash-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="wash-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo lavaggio</Button>
      </PageHeader>
      {isAdmin && <StationTabs stations={stations} value={stationId} onChange={setStationId} prefix="wash" />}
      <PeriodPicker value={period} onChange={setPeriod} prefix="wash" />
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <KpiCard label="Lavaggi nel periodo" value={s.total ?? 0} accent="#0F172A" testId="wash-kpi-total" />
        <KpiCard label="Costo nel periodo" value={eur(s.cost)} accent="#0369A1" testId="wash-kpi-cost" />
        <KpiCard label="Furgoni lavati" value={s.vehicles ?? 0} accent="#059669" testId="wash-kpi-vehicles" />
      </div>
      <div className="relative mb-3 w-full max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa, luogo, note..." className="pl-9" data-testid="wash-search-input" />
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wash-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="wash_date">Data</S><S k="plate">Targa</S><S k="place">Luogo</S><S k="wash_type">Tipo</S><S k="cost" align="right">Costo</S><th className="px-3 py-2">Note</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50" data-testid={`wash-row-${i.id}`}>
                  <td className="whitespace-nowrap px-3 py-2">{fd(i.wash_date)}</td>
                  <td className="whitespace-nowrap px-3 py-2"><span className="font-mono-num rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold">{i.plate}</span>{isAdmin && <span className="ml-2 text-xs text-slate-500">{i.station_name}</span>}</td>
                  <td className="px-3 py-2">{i.place}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs"><span className="rounded-full bg-sky-50 px-2 py-0.5 font-semibold text-sky-800">{TYPE[i.wash_type]}</span></td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 text-right font-semibold">{i.cost == null ? "—" : eur(i.cost)}</td>
                  <td className="min-w-[10rem] px-3 py-2 text-xs text-slate-600">{i.notes}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`wash-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(i)} data-testid={`wash-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500" data-testid="wash-empty">Nessun lavaggio nel periodo.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <WashDialog item={editing} stations={stations} vehicles={vehicles} isAdmin={isAdmin} places={places} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
